import type { TypedDocumentNode } from '@apollo/client';
import {
  initialPagedGridInput,
  PAGED_LIST_FETCH_POLICY,
  type PagedGridQueryState,
  usePreloadedServerGridQuery,
} from 'src/modules/grids';
import { preloadRouteQuery } from 'src/routing/routePreload';
import type { AppRouterContext } from 'src/routing/routerAccess';
import type { BusinessEntityPageQuery, BusinessEntityPageQueryVariables } from 'types/graphql';

/**
 * Where the Business Entities grid persists its layout. Kept at v2 across the move to paging: every
 * field a saved v2 layout sorts or filters on is answered by the server (an account sort lists by name).
 */
export const BUSINESS_ENTITIES_GRID_STORAGE_KEY = 'businessEntities_v2';

/** The Business Entities grid's bounded read (TAS-3660): one server page per request, same node selection as before. */
const BUSINESS_ENTITY_PAGE_QUERY: TypedDocumentNode<BusinessEntityPageQuery, BusinessEntityPageQueryVariables> = gql`
  query BusinessEntityPageQuery($input: StandardPageInput!) {
    businessEntityPage(input: $input) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
          roles
          bluebookId
          disabled
          customFields

          payablesAccount {
            id
            accountName
            accountNumber
          }

          receivablesAccount {
            id
            accountName
            accountNumber
          }
        }
      }
    }
  }
`;

export type BusinessEntityRow = NonNullable<
  NonNullable<NonNullable<BusinessEntityPageQuery['businessEntityPage']['edges']>[number]>['node']
>;

export function useBusinessEntityPage(gridState: PagedGridQueryState) {
  return usePreloadedServerGridQuery({
    queryKey: 'businessEntityPage',
    query: BUSINESS_ENTITY_PAGE_QUERY,
    gridState,
    label: 'business entities',
  });
}

/** The grid's first page as it will ask for it; the Business Entities tab preloads it on intent. */
export function preloadBusinessEntitiesGrid(context: AppRouterContext) {
  const input = initialPagedGridInput(BUSINESS_ENTITIES_GRID_STORAGE_KEY);
  return preloadRouteQuery(context, BUSINESS_ENTITY_PAGE_QUERY, { input }, { fetchPolicy: PAGED_LIST_FETCH_POLICY });
}
